import React, { useEffect, useRef, useState } from "react";
import ApexCharts from "apexcharts";
import Select, { SingleValue } from "react-select";

interface ChartData {
  id: string;
  selector: string;
  data: { x: string; y: number }[];
  rendered: boolean;
}

interface UsagesGraphComponentProps {
  creationTimestamps: string[];
  title: string;
  selectId: string;
}

/** Detect current Bootstrap theme (light/dark) and re-render on change. */
const useBsTheme = (): "light" | "dark" => {
  const getTheme = (): "light" | "dark" => {
    if (typeof document === "undefined") return "light";
    return document.documentElement.getAttribute("data-bs-theme") === "dark"
      ? "dark"
      : "light";
  };
  const [theme, setTheme] = useState<"light" | "dark">(getTheme);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const observer = new MutationObserver(() => setTheme(getTheme()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bs-theme"],
    });
    return () => observer.disconnect();
  }, []);

  return theme;
};

const UsagesGraphComponent: React.FC<UsagesGraphComponentProps> = ({
  creationTimestamps,
  title,
  selectId,
}) => {
  const [activeTab, setActiveTab] = useState<string>("all");
  const [filteredData, setFilteredData] = useState<ChartData[]>([]);
  const chartInstanceRef = useRef<ApexCharts | null>(null);
  const theme = useBsTheme();

  /* -------------- grouping (unchanged) -------------- */
  const groupDataBy = (timestamps: string[], period: string) => {
    const groupedData: { [key: string]: number } = {};
    const now = new Date();

    timestamps.forEach((timestamp) => {
      const date = new Date(timestamp);
      let key = "";

      if (period === "day") {
        if (
          date.toISOString().split("T")[0] === now.toISOString().split("T")[0]
        ) {
          key = date.toISOString().split("T")[0];
        }
      } else if (period === "week") {
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - 6);
        weekStart.setHours(0, 0, 0, 0);
        if (date >= weekStart && date <= now) {
          key = date.toISOString().split("T")[0];
        }
      } else if (period === "month") {
        const monthStart = new Date(now);
        monthStart.setDate(now.getDate() - 29);
        monthStart.setHours(0, 0, 0, 0);
        if (date >= monthStart && date <= now) {
          key = date.toISOString().split("T")[0];
        }
      } else if (period === "year") {
        const yearStart = new Date(now);
        yearStart.setFullYear(now.getFullYear() - 1);
        yearStart.setHours(0, 0, 0, 0);
        if (date >= yearStart && date <= now) {
          key = date.toISOString().split("T")[0];
        }
      } else if (period === "all") {
        key = date.toISOString().split("T")[0];
      }

      if (key) {
        groupedData[key] = (groupedData[key] || 0) + 1;
      }
    });

    return Object.entries(groupedData).map(([x, y]) => ({ x, y }));
  };

  /* -------------- chart init (theme-aware + proper cleanup) -------------- */
  const initChart = (chart: ChartData) => {
    const element = document.querySelector(chart.selector) as HTMLElement;
    if (!element) return;

    // Clean previous instance to prevent stacking / memory leaks
    if (chartInstanceRef.current) {
      try {
        chartInstanceRef.current.destroy();
      } catch (_e) {
        /* noop */
      }
      chartInstanceRef.current = null;
    }
    element.innerHTML = "";

    const isDark = theme === "dark";
    const brand = "#ea580c";
    const brandLight = "#fb923c";
    const gridColor = isDark ? "rgba(255,255,255,0.08)" : "#eef0f2";
    const axisLabelColor = isDark ? "#b8b2ac" : "#8a857f";
    const axisLineColor = isDark ? "rgba(255,255,255,0.10)" : "#e8e8e8";

    const options: any = {
      series: [{ name: title, data: chart.data }],
      chart: {
        type: "area",
        height: 280,
        toolbar: { show: false },
        fontFamily: "inherit",
        background: "transparent",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 600,
        },
      },
      colors: [brand],
      dataLabels: { enabled: false },
      fill: {
        type: "gradient",
        gradient: {
          shade: isDark ? "dark" : "light",
          shadeIntensity: 1,
          opacityFrom: isDark ? 0.45 : 0.35,
          opacityTo: 0,
          stops: [0, 90, 100],
          colorStops: [
            { offset: 0, color: brandLight, opacity: isDark ? 0.45 : 0.35 },
            { offset: 100, color: brand, opacity: 0 },
          ],
        },
      },
      stroke: { curve: "smooth", width: 2.5 },
      xaxis: {
        categories: chart.data.map((item) => item.x),
        labels: {
          style: { colors: axisLabelColor, fontSize: "11px", fontWeight: 500 },
        },
        axisBorder: { color: axisLineColor },
        axisTicks: { color: axisLineColor },
      },
      yaxis: {
        tickAmount: 4,
        labels: {
          style: { colors: axisLabelColor, fontSize: "11px", fontWeight: 500 },
        },
      },
      grid: {
        show: true,
        borderColor: gridColor,
        strokeDashArray: 5,
        padding: { left: 8, right: 8, top: 0, bottom: 0 },
      },
      markers: {
        size: 0,
        strokeColors: brand,
        strokeWidth: 2,
        hover: { size: 6 },
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        style: { fontSize: "12px" },
        y: { formatter: (val: number) => `${val}` },
      },
    };

    const apexChart = new ApexCharts(element, options);
    apexChart.render();
    chartInstanceRef.current = apexChart;
    chart.rendered = true;
  };

  useEffect(() => {
    const data = groupDataBy(creationTimestamps, activeTab);
    setFilteredData([
      {
        id: activeTab,
        selector: `#chart_${activeTab}_${selectId}`,
        data,
        rendered: false,
      },
    ]);
    // eslint-disable-next-line
  }, [activeTab, creationTimestamps]);

  /* Re-render chart on theme change by flipping rendered flag */
  useEffect(() => {
    setFilteredData((prev) => prev.map((c) => ({ ...c, rendered: false })));
  }, [theme]);

  useEffect(() => {
    filteredData.forEach((chart) => {
      if (!chart.rendered) {
        initChart(chart);
      }
    });
    // eslint-disable-next-line
  }, [filteredData]);

  /* Cleanup on unmount */
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        try {
          chartInstanceRef.current.destroy();
        } catch (_e) {
          /* noop */
        }
        chartInstanceRef.current = null;
      }
    };
  }, []);

  const handleDateSelection = (
    selectedOption: SingleValue<{ label: string; value: string }>
  ) => {
    setActiveTab(selectedOption?.value || "all");
  };

  return (
    <div className="ni-usage-graph-card">
      <div className="ni-usage-graph-card-header">
        <h3 className="ni-usage-graph-card-title">
          <div className="ni-usage-graph-card-title-icon" aria-hidden="true">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          {title}
        </h3>
        <div className="ni-usage-graph-card-toolbar">
          <Select
            defaultValue={{ label: "All", value: "all" }}
            className="react-select-styled w-125px p-0 react-select-sm border-select"
            classNamePrefix="react-select"
            options={[
              { label: "Day", value: "day" },
              { label: "Week", value: "week" },
              { label: "Month", value: "month" },
              { label: "Year", value: "year" },
              { label: "All", value: "all" },
            ]}
            onChange={handleDateSelection}
          />
        </div>
      </div>
      <div className="ni-usage-graph-card-body">
        {filteredData.map((chart) => (
          <div
            key={chart.id}
            className={`tab-pane fade ${
              activeTab === chart.id ? "active show" : ""
            }`}
          >
            <div
              id={chart.selector.replace("#", "")}
              className="ni-usage-graph-canvas"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default UsagesGraphComponent;
